import { useEffect, useState } from "react";
import { Loader2, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const useJobPoll = (job, onDone) => {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!job?.job_id) return undefined;
    const t = setInterval(async () => {
      const { data } = await api.get(`/jobs/${job.job_id}`);
      setState(data);
      if (data.status === "done") { clearInterval(t); onDone?.(data); }
    }, 1500);
    return () => clearInterval(t);
  }, [job?.job_id]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
};

export const MassReassignDialog = ({ open, onOpenChange, sellers, selection, onDone }) => {
  const [seller, setSeller] = useState("");
  const [saving, setSaving] = useState(false);
  const [job, setJob] = useState(null);
  const [summary, setSummary] = useState(null);
  const progress = useJobPoll(job, (d) => { setSummary(d); onDone?.(); });
  const active = sellers.filter((s) => s.active !== false);

  useEffect(() => { if (open) { setSeller(""); setJob(null); setSummary(null); } }, [open]);

  const run = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/leads/mass-reassign", { seller_id: seller, ...selection.body });
      if (data.status === "running") setJob(data);
      else { setSummary(data); onDone?.(); }
    } catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };
  const target = active.find((s) => s.id === seller);

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="mass-reassign-dialog">
        <DialogHeader>
          <DialogTitle>Reasignar leads</DialogTitle>
          <DialogDescription>{selection.count.toLocaleString("es-EC")} leads seleccionados</DialogDescription>
        </DialogHeader>
        {summary ? (
          <p className="rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="mass-reassign-summary">
            {summary.reassigned} leads reasignados{summary.skipped ? ` · ${summary.skipped} sin cambios (ya eran de ese vendedor)` : ""}
          </p>
        ) : job ? (
          <div className="space-y-2" data-testid="mass-reassign-progress">
            <Progress value={progress ? (progress.processed / progress.total) * 100 : 0} className="h-2" />
            <p className="text-sm text-slate-600">{progress?.processed || 0} / {job.total} procesados…</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Select value={seller || undefined} onValueChange={setSeller}>
              <SelectTrigger className="rounded-xl" data-testid="mass-reassign-seller-select"><SelectValue placeholder="Vendedor destino" /></SelectTrigger>
              <SelectContent>{active.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            {target && <p className="text-sm text-slate-600" data-testid="mass-reassign-confirm-text">¿Reasignar {selection.count.toLocaleString("es-EC")} leads a <b>{target.name}</b>? Sus conversaciones, citas y tareas abiertas pasan al nuevo vendedor.</p>}
          </div>
        )}
        <DialogFooter>
          {summary || job ? (
            <Button className="rounded-xl" onClick={() => onOpenChange(false)} disabled={job && !summary} data-testid="mass-reassign-close">Cerrar</Button>
          ) : (
            <>
              <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)} data-testid="mass-reassign-cancel">Cancelar</Button>
              <Button className="rounded-xl bg-[#1E6FD9]" disabled={!seller || saving} onClick={run} data-testid="mass-reassign-confirm">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Shuffle className="mr-1.5 h-4 w-4" />} Confirmar
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
